import React from 'react';
import {
  PdfButton,
  InputContainer,
  NumPages,
  Center,
  Left,
  PageInput,
  PdfToolbar as Toolbar,
  VerticalBar,
  Right,
} from './newPdf.style';
import Icon from 'components/Input/Icon';
import { colorPalette, colorPaletteDark } from 'utils/styleConstants';
import fileHelper from 'utils/helpers/file.helper';
import { useTheme } from 'providers/ThemeProvider';

export type PdfButtonProps = {
  showFitButtons?: boolean;
  showRotateButtons?: boolean;
  showPageNavigation?: boolean;
  showZoomButtons?: boolean;
  showDownloadButton?: boolean;
  showOcrButtons?: boolean;
  showMappingButtons?: boolean;
  showTranslateButtons?: boolean;
};
export interface PdfToolbarProps extends PdfButtonProps {
  base64: string;
  pageNumber: number;
  setPageNumber: (num: number) => void;
  numPages: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotateClockwise: () => void;
  onRotateCounterClockwise: () => void;
  onFitToWidth: () => void;
  onFitToHeight: () => void;
  fitMode: 'width' | 'height' | null;
  ocrMode: boolean;
  ocrModeType: 'area' | 'click';
  setOcrModeType: (val: 'area' | 'click') => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onFirstPage: () => void;
  onLastPage: () => void;
  onToggleMappingMode: () => void;
  translateDoc?: () => void;
  translationTooltip?: {
    title?: string;
    color?: string;
  };
  canZoomIn?: boolean;
  canZoomOut?: boolean;
}

const PdfToolbar: React.FC<PdfToolbarProps> = ({
  pageNumber,
  numPages,
  onZoomIn,
  onZoomOut,
  onRotateClockwise,
  onRotateCounterClockwise,
  onFitToWidth,
  onFitToHeight,
  onPrevPage,
  onNextPage,
  onFirstPage,
  onLastPage,
  setPageNumber,
  base64,
  fitMode,
  ocrMode,
  onToggleMappingMode,
  ocrModeType,
  setOcrModeType,
  showFitButtons = true,
  showRotateButtons = true,
  showPageNavigation = true,
  showZoomButtons = true,
  showDownloadButton = true,
  showOcrButtons = true,
  showMappingButtons = true,
  showTranslateButtons = true,
  translateDoc,
  translationTooltip,
  canZoomIn,
  canZoomOut,
}) => {
  const { isDarkMode } = useTheme();

  return (
    <Toolbar>
      <Left>
        {showFitButtons &&
          (fitMode === 'height' ? (
            <PdfButton style={{ color: 'grey' }} disabled={false} onClick={onFitToWidth}>
              <Icon icon="arrow-alt-v" rotate={90} size="xl" style={{ color: 'grey' }} />
            </PdfButton>
          ) : (
            <PdfButton style={{ color: 'grey' }} disabled={false} onClick={onFitToHeight}>
              <Icon icon="arrow-alt-v" size="xl" style={{ color: 'grey' }} />
            </PdfButton>
          ))}
      </Left>
      <Center>
        <>
          {showRotateButtons && (
            <>
              <PdfButton disabled={false} onClick={onRotateCounterClockwise}>
                <Icon icon="undo-alt" size="xl" />
              </PdfButton>
              <PdfButton disabled={false} onClick={onRotateClockwise}>
                <Icon icon="redo-alt" size="xl" />
              </PdfButton>
              <VerticalBar />
            </>
          )}
          {showPageNavigation && (
            <>
              <Icon icon="top-page" disabled={pageNumber === 1} onClick={onFirstPage} />
              <PdfButton disabled={pageNumber <= 1} onClick={onPrevPage}>
                <Icon icon="angle-up" size="xl" disabled={pageNumber <= 1} />
              </PdfButton>
              <InputContainer>
                <PageInput onChange={(e: any) => setPageNumber(Number(e.target.value))} value={pageNumber} />
                <NumPages>/{numPages}</NumPages>
              </InputContainer>
              <PdfButton disabled={pageNumber >= numPages} onClick={onNextPage}>
                <Icon icon="angle-up" rotate={180} size="xl" disabled={pageNumber >= numPages} />
              </PdfButton>
              <Icon icon="top-page" rotate={180} disabled={pageNumber === numPages} onClick={onLastPage} />
              <VerticalBar />
            </>
          )}
          {showZoomButtons && (
            <>
              <PdfButton disabled={!canZoomOut} onClick={onZoomOut}>
                <Icon icon="searchMinus" size="xl" disabled={!canZoomOut} />
              </PdfButton>
              <PdfButton disabled={!canZoomIn} onClick={onZoomIn}>
                <Icon icon="search-plus" size="xl" disabled={!canZoomIn} />
              </PdfButton>
            </>
          )}
        </>
      </Center>
      <Right>
        <>
          {showOcrButtons && (
            <>
              <PdfButton>
                <Icon icon="ocr" disabled={!ocrMode} size="xl" />
              </PdfButton>
              <PdfButton onClick={() => setOcrModeType('area')}>
                <Icon icon="ocr-area" disabled={ocrModeType !== 'area'} size="xl" />
              </PdfButton>
              <PdfButton onClick={() => setOcrModeType('click')}>
                <Icon icon="ocr-click" disabled={ocrModeType !== 'click'} size="xl" />
              </PdfButton>
            </>
          )}
          {showMappingButtons && (
            <PdfButton onClick={onToggleMappingMode}>
              <Icon icon="object-ungroup" size="xl" />
            </PdfButton>
          )}

          {showDownloadButton && (
            <>
              <VerticalBar />
              <PdfButton style={{ color: colorPalette.grey.grey9 }} onClick={() => fileHelper.openPdfInNewTab(base64)}>
                <Icon
                  icon="file-pdf"
                  size="xl"
                  style={{ color: isDarkMode ? colorPaletteDark.turquoise.accent : colorPalette.turquoise.normal }}
                />
              </PdfButton>
            </>
          )}
        </>
        {showTranslateButtons && (
          <PdfButton
            style={{ color: colorPalette.grey.grey9 }}
            disabled={false}
            onClick={translateDoc}
            title={translationTooltip?.title}
          >
            <Icon
              icon="globe"
              size="xl"
              style={{ color: isDarkMode ? colorPaletteDark.turquoise.accent : colorPalette.turquoise.normal }}
            />
          </PdfButton>
        )}
      </Right>
    </Toolbar>
  );
};

export default PdfToolbar;
